import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { useMutation } from "@tanstack/react-query";
import cx from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Form,
  LoaderFunction,
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router-dom";

import { get, post, rawGet } from "api";
import { Button } from "atoms/button/Button";
import { Dropdown } from "atoms/dropdown/Dropdown";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import Info from "atoms/icon/icons/info.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { TextField } from "atoms/text-field/TextField";
import { useCreateMultiPlatformUser } from "hooks/useCreateMultiPlatformUser";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { queryClient } from "pages/Root";
import Services from "services/Services";
import { currencyService } from "toolbox/CurrencyService";
import { waitObj } from "toolbox/Promise";
import { changeDayjsLocale, formatToLongDate } from "utils/dayjsUtils";
import { isValidDateFormat, isValidEmail } from "utils/validators";

interface FormErrorStates {
  firstName?: boolean;
  lastName?: boolean;
  email?: boolean;
  dob?: boolean;
  location?: boolean;
  referral?: boolean;
}

const getLocationsMobile = async (platformId?: string | null) => {
  if (!platformId) {
    throw new Error("Missing required platformId search param");
  }

  const { locations } = await get(
    "/multi-platform/platform/{platformId}/locations",
    {
      platformId,
    }
  );

  return locations;
};

const load = async (request: Request) => {
  const authToken = await Services.auth.loadToken().then(
    (token) =>
      token ??
      Preferences.get({
        key: "multiPlatformVerifyToken",
      }).then(({ value }) => value)
  );
  const user = await queryClient
    .fetchQuery(["/user"], () =>
      rawGet(
        "/user",
        undefined,
        authToken ? { Authorization: `Bearer ${authToken}` } : undefined
      )
    )
    .catch(() => null);

  const searchParams = new URL(request.url).searchParams;
  const platformId =
    searchParams.get("platformId") ?? user?.platformDomain?.platformId;
  const referralCode = searchParams.get("referralCode");
  const platformImageUrl = searchParams.get("platformImageUrl");
  const locationId = searchParams.get("locationId");

  const locations = Capacitor.isNativePlatform()
    ? getLocationsMobile(platformId)
    : get("/platform/locations", {}).then(({ items }) => items ?? []);

  const phoneNumber = Preferences.get({ key: "phoneNumber" }).then(
    ({ value }) => value
  );

  const platform = queryClient
    .fetchQuery({
      queryKey: ["/platform/current"],
      queryFn: () => get("/platform/current"),
      staleTime: 0,
    })
    // TODO: This query throws errors in anonymous contexts on mobile
    .catch(() => null)
    .then((platform) => {
      changeDayjsLocale(platform?.stripeRegion);
      currencyService.setCurrency(platform?.stripeRegion);
      return platform;
    });

  return waitObj({
    platformId,
    platform,
    referralCode,
    platformImageUrl,
    locationId,
    user,
    locations,
    phoneNumber,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const completeProfileLoader: LoaderFunction = ({ request }) => {
  return load(request);
};

export const CompleteProfilePage = () => {
  const {
    platformId,
    platform,
    referralCode,
    platformImageUrl,
    locationId,
    user,
    locations,
    phoneNumber,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const navigation = useNavigation();
  const locationState = useLocation();
  const createMultiPlatformUserMutation = useCreateMultiPlatformUser();

  const [dob, setDob] = useState(
    user?.dateOfBirth ? dayjs(user.dateOfBirth).format("L") : ""
  );

  const preRegisteredLocationId = user?.hasBeenPreRegistered
    ? user?.platformLocation?.id
    : null;
  const [locationValue, setLocationValue] = useState(
    locationId ?? preRegisteredLocationId ?? ""
  );
  const [locationValueText, setLocationValueText] = useState(
    locations.find(({ id }) => id === locationValue)?.name ?? ""
  );
  const [isBirthdayModalOpen, setIsBirthdayModalOpen] = useState(false);

  const referralOptions = [
    { name: "From an Employee", id: "employee" },
    { name: "Email List", id: "email-list" },
    { name: "Facebook", id: "facebook" },
    { name: "Instagram", id: "instagram" },
    { name: "TikTok", id: "tiktok" },
    { name: "Through a Friend", id: "friend" },
    { name: "Other", id: "other" },
  ];

  const initialReferralValValue =
    user?.hasBeenPreRegistered && !!user.signUpSource ? user.signUpSource : "";
  const [referralValue, setReferralValue] = useState(initialReferralValValue);
  const [referralValueText, setReferralValueText] = useState(
    referralOptions.find(({ id }) => id === referralValue)?.name ?? ""
  );

  const userInformation = {
    birthday: user?.dateOfBirth ?? "",
    firstName: user?.firstName ?? "",
    lastName: user?.lastName ?? "",
    email: user?.email ?? "",
    platformLocationId: user?.platformLocation?.id ?? "",
    referral: initialReferralValValue,
    referrerName: (user?.hasBeenPreRegistered && user.referrerName) || "",
  };

  const [formData, setFormData] = useState(userInformation);
  const [errors, setErrors] = useState<FormErrorStates>({});

  //const { dateLocale, formatDate } = useDateLocalization();

  const validateForm = () => {
    const formErrors: FormErrorStates = {};
    let isValid = true;

    if (!formData.firstName || formData.firstName.trim() === "") {
      formErrors.firstName = true;
      isValid = false;
    }

    if (!formData.lastName || formData.lastName.trim() === "") {
      formErrors.lastName = true;
      isValid = false;
    }

    if (!isValidEmail(formData.email)) {
      formErrors.email = true;
      isValid = false;
    }

    if (dob && (dob.length < 10 || !isValidDateFormat(dob))) {
      formErrors.dob = true;
      isValid = false;
    }

    if (locations.length > 1 && !!locationValue === false) {
      formErrors.location = true;
      isValid = false;
    }

    if (!referralValue && !referralCode) {
      formErrors.referral = true;
      isValid = false;
    }

    setErrors(formErrors);

    return isValid;
  };

  const webMutation = useMutation({
    mutationFn: async () => {
      return post("/user/update", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        ...(formData.birthday ? { dateOfBirth: formData.birthday } : {}),
        signUpSource: referralCode ? "friend" : referralValue,
        platformLocationId:
          locations.length === 1 ? locations[0].id : locationValue,
        referrerName:
          referralValue == "employee" ||
          referralValue == "other" ||
          referralValue == "friend"
            ? formData.referrerName
            : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/user"] });
      if (user?.v1Id) {
        // user is migrated v1, no account created/offer
        navigate("/signin/friend-referral");
      } else {
        navigate("/signin/account-created");
      }
    },
  });

  const handleSubmit = () => {
    if (!validateForm()) return;

    if (Capacitor.isNativePlatform()) {
      const userToCreate = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        ...(formData.birthday ? { dateOfBirth: formData.birthday } : {}),
        signUpSource: referralCode ? "friend" : referralValue,
        platformLocationId:
          locations.length === 1 ? locations[0].id : locationValue,
        referrerName:
          referralValue == "employee" ||
          referralValue == "other" ||
          referralValue == "friend"
            ? formData.referrerName
            : null,
        phoneNumber: phoneNumber,
        signupReferralCode: referralCode,
        userId: user?.id,
        platformId,
      };
      createMultiPlatformUserMutation.mutate(userToCreate);
    } else {
      webMutation.mutate();
    }
  };

  return (
    <div>
      {platformImageUrl && (
        <div className="sticky top-0 w-full bg-white px-3 pt-[env(safe-area-inset-top)]">
          <span className="flex items-center justify-between">
            <IconButton
              size="small"
              style="iconOnly"
              svg={ChevronLeft}
              onClick={() =>
                locationState?.state
                  ? navigate("/signin/signup/find-medspa/search-location", {
                      state: locationState.state,
                    })
                  : navigate(-1)
              }
            />

            <img src={platformImageUrl} className="h-8" />

            <div className="invisible">
              <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
            </div>
          </span>
        </div>
      )}

      <Form method="post">
        <div
          className={cx(
            "flex flex-col items-center gap-4 px-8 pb-4",
            platformImageUrl
              ? "pt-6"
              : "pt-[calc(24px_+_env(safe-area-inset-top))]"
          )}
        >
          <div className="mt-8 font-serif text-h2 text-primary">
            Complete your profile
          </div>
          <div className="text-center text-body2 text-secondary">
            Tell us a bit more about yourself
          </div>
          <fieldset
            disabled={navigation.state === "submitting"}
            className="flex w-full flex-col pb-5"
          >
            <div className="flex flex-col gap-[0.8rem]">
              <input type="hidden" name="location" value={locationValue} />
              <input type="hidden" name="referral" value={referralValue} />
              <div className="flex gap-3">
                <TextField
                  type="text"
                  label="First Name"
                  name="firstName"
                  autoComplete="given-name"
                  invalid={errors.firstName}
                  value={formData.firstName}
                  onChange={(value) => {
                    setFormData({ ...formData, firstName: value });
                    setErrors({ ...errors, firstName: false });
                  }}
                />
                <TextField
                  type="text"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={formData.lastName}
                  invalid={!!errors.lastName}
                  onChange={(value) => {
                    setFormData({ ...formData, lastName: value });
                    setErrors({ ...errors, lastName: false });
                  }}
                />
              </div>
              <TextField
                type="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                invalid={errors.email}
                onChange={(value) => {
                  setFormData({ ...formData, email: value });
                }}
                onBlur={(value) => {
                  const isValid = !isValidEmail(value);
                  setErrors({ ...errors, email: isValid });
                }}
              />
              <div className="w-[80%]">
                <div className="flex gap-3">
                  <TextField
                    type="decimal"
                    label="Date of birth (optional)"
                    name="dob"
                    autoComplete="bday"
                    value={dob}
                    maxLength={10}
                    placeholder={dayjs.localeData().longDateFormat("L")}
                    pattern="[0-9]*"
                    invalid={errors.dob}
                    disabled={
                      user?.dateOfBirth != null &&
                      user?.dateOfBirth.trim() != ""
                    }
                    onChange={(value: string) => {
                      const formatted = formatToLongDate(value);
                      setDob(formatted);
                      if (value.length >= 10 && isValidDateFormat(value)) {
                        setFormData({
                          ...formData,
                          birthday: new Date(value).toISOString(),
                        });
                        setErrors({ ...errors, dob: false });
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-1 text-black "
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsBirthdayModalOpen(true);
                    }}
                  >
                    <Icon svg={Info} size="small" color="secondaryText" />
                  </button>
                  <ListModal
                    open={isBirthdayModalOpen}
                    onClose={() => setIsBirthdayModalOpen(false)}
                    size="small"
                    title="Date of Birth"
                    firstButtonLabel="Got it"
                    onFirstButtonClick={() => setIsBirthdayModalOpen(false)}
                  >
                    <>
                      <div
                        key="service-fee"
                        className={cx(
                          "mx-6 flex justify-between border-t border-light-grey pt-6"
                        )}
                      >
                        <div className="text-bold1 capitalize">
                          Why Share Your Date of Birth?
                        </div>
                      </div>
                      <div className="px-6 pt-2 text-body2 text-secondary">
                        Unlock exclusive birthday rewards and personalized
                        discounts! While sharing your date of birth is optional,
                        it ensures you won’t miss out on special offers just for
                        you.
                      </div>
                    </>
                  </ListModal>
                </div>
                <span className="ml-1 text-center text-body2 text-secondary">
                  You’ll get a bday gift!
                </span>
              </div>
              {locations.length > 1 && (
                <div className="pt-2">
                  <Dropdown
                    disabled={user?.hasBeenPreRegistered}
                    label="Nearest location"
                    options={locations}
                    value={locationValue}
                    setValue={(value) => {
                      setLocationValue(value);
                      setErrors({ ...errors, location: false });
                    }}
                    textValue={locationValueText}
                    setTextValue={setLocationValueText}
                    invalid={errors.location}
                  />
                </div>
              )}
              {!referralCode && (
                <div className="pt-2">
                  <Dropdown
                    disabled={user?.hasBeenPreRegistered && !!user.signUpSource}
                    label="How did you hear about us?"
                    options={referralOptions}
                    value={referralValue}
                    textValue={referralValueText}
                    setTextValue={setReferralValueText}
                    invalid={!!errors.referral}
                    setValue={(value) => {
                      setReferralValue(value);
                      setErrors({ ...errors, referral: false });
                    }}
                  />
                </div>
              )}
              {((referralValue == "employee" &&
                platform?.platformSettings?.employeeNameReferralEnabled) ||
                referralValue == "other" ||
                referralValue == "friend") && (
                <div>
                  <TextField
                    disabled={user?.hasBeenPreRegistered && !!user.signUpSource}
                    type="text"
                    label={
                      referralValue == "employee"
                        ? "Employee Name"
                        : referralValue == "other"
                        ? "Other"
                        : referralValue == "friend"
                        ? "Friend Name"
                        : undefined
                    }
                    name="referral"
                    value={formData.referrerName}
                    onChange={(value) =>
                      setFormData({ ...formData, referrerName: value })
                    }
                  />
                </div>
              )}
              <div className="pt-10">
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  disabled={
                    webMutation.isLoading ||
                    createMultiPlatformUserMutation.isLoading
                  }
                >
                  Complete profile
                </Button>
              </div>
            </div>
          </fieldset>
        </div>
      </Form>
    </div>
  );
};
